import React, { useContext, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import classNames from 'classnames'
import { v4 as uuidv4 } from 'uuid'

import ModalContext from '../context/ModalContext'
import TmoH2 from '../tmoControlWrappers/TmoH2'
import TmoSvg from '../tmoUiHelpers/TmoSvg'
import { keyCodes } from '../tmoUtilities/keyboard'

import style from './ModalV2.module.css'

function ModalV2({
  className,
  children,
  title,
  type,
  resetModal,
  portal = true,
  id,
  label,
  modalType = '',
  modalSize = 'lg',
  testId
}) {
  const overlayRef = useRef()
  const generatedId = uuidv4()
  const { isModalOpen, closeModal } = useContext(ModalContext)
  const [showAnimation, setShowAnimation] = useState(false)
  const [isModalVisible, setModalVisible] = useState(false)

  useEffect(() => {
    if (!isModalOpen) {
      return
    }

    const closeModalOnEsc = (event) => {
      if (event.keyCode === keyCodes.ESCAPE) {
        resetModal && resetModal()
        closeModal()
      }
    }

    document.addEventListener('keydown', closeModalOnEsc, false)

    return () => {
      document.removeEventListener('keydown', closeModalOnEsc, false)
    }
  }, [closeModal, isModalOpen, resetModal])

  useEffect(() => {
    if (modalType === 'slide') {
      if (isModalOpen) {
        setModalVisible(true)
        document.body.style.overflow = 'hidden'
        isModalVisible &&
          setTimeout(() => {
            setShowAnimation(true)
          }, 50)
      } else {
        document.body.style.overflow = 'unset'
        setShowAnimation(false)
        setTimeout(() => {
          setModalVisible(false)
        }, 400)
      }
    } else {
      isModalOpen ? setModalVisible(true) : setModalVisible(false)
    }
    isModalOpen && overlayRef?.current?.focus()
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isModalOpen, isModalVisible, modalType])

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current && modalType !== 'slide') {
      resetModal && resetModal()
      closeModal()
    }
  }

  useEffect(() => {
    const trapFocusInsideModal = (event) => {
      if (overlayRef.current) {
        const focusableElements = Array.from(
          overlayRef.current.querySelectorAll(
            'a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])'
          )
        ).filter(
          (el) =>
            !el.hasAttribute('disabled') &&
            !el.getAttribute('aria-hidden') &&
            el.tagName.toLowerCase() !== 'use'
        )

        const firstElement = focusableElements[0]
        const lastElement = focusableElements[focusableElements.length - 1]
        if (event.key === 'Tab') {
          if (event.shiftKey && document.activeElement === firstElement) {
            event.preventDefault()
            lastElement.focus()
          } else if (
            !event.shiftKey &&
            document.activeElement === lastElement
          ) {
            event.preventDefault()
            firstElement.focus()
          }
        }
      }
    }

    if (isModalOpen) {
      document.addEventListener('keydown', trapFocusInsideModal, false)
    } else {
      document.removeEventListener('keydown', trapFocusInsideModal, false)
    }

    return () => {
      document.removeEventListener('keydown', trapFocusInsideModal, false)
    }
  }, [isModalOpen])

  const content = isModalVisible ? (
    <div
      className={classNames(style.modal, className, style[modalType])}
      ref={overlayRef}
      onClick={handleOverlayClick}
      role='dialog'
      id={id || generatedId}
      aria-labelledby={`title${generatedId}`}
      aria-modal='true'
      data-testid={testId}
      tabIndex='-1'
    >
      <div
        className={classNames(
          style.container,
          style[modalSize],
          showAnimation ? style.show : null
        )}
        data-testid={'modalContainer'}
      >
        <div
          className={classNames(style.header, style[type])}
          data-testid={'modalClose'}
        >
          <TmoH2 id={`title${generatedId}`} className={style.title}>
            <span title={title}> {title} </span>
          </TmoH2>
          <TmoSvg
            svgId='x'
            className={style.close}
            aria-label={'Close Modal'}
            onClick={() => {
              resetModal && resetModal()
              closeModal()
            }}
          />
        </div>
        <div className={style.content}>{children}</div>
      </div>
    </div>
  ) : null

  return portal ? createPortal(content, document.body) : content
}

export default ModalV2
